/**
 * LocationEditorPage — the single-location record.
 *
 * Rebuilt 2026-08-07 (Nick) from `Locations - Admin & Bulk Creation.dc.html`
 * option 1b, for the System Administrator persona in all three verticals.
 *
 * The two things this screen exists to get right:
 *
 * 1. **The address form follows the country.** Labels, ORDER and required-ness
 *    all come from the contract's ADDRESS_FORMATS — Japan leads with the postal
 *    code and has no street name, Germany puts PLZ before Ort, the UK's county
 *    is optional, India requires a locality. Nothing about countries is decided
 *    in this file; it renders whatever `addressRows(country)` returns.
 *
 * 2. **Per-field validation with real messages.** Every field validates on blur
 *    and again on save, through the SAME validator the bulk importer and the
 *    serving engine use (`location-validation` → `@v0y4ge/api-contracts`). Save
 *    is blocked while anything is invalid, and a rejected save from the server
 *    re-renders as field errors rather than a bare toast.
 *
 * Writes go through the typed client and are awaited before any success
 * feedback (no-fake-interactivity mandate).
 */
import { useState, useEffect, useMemo, useRef, type CSSProperties, type ReactNode } from "react";
import { Btn, Icon, Input, T, TimeZonePicker } from "@v0y4ge/jrni-ui";
import { createLocation, updateLocation } from "@v0y4ge/api-client";
import { LOCATIONS_OPTS } from "../../shell/request-opts";
import { logAction } from "../../lib/audit";
import { toast } from "../../shell/toast";
import { Screen, PageHeader } from "../../shell/chrome";
import { can } from "../../shell/access";
import { useNav } from "../../shell/router";
import { useLocations } from "./useLocations";
import LocationMap from "./LocationMap";
import { validateAddress } from "@v0y4ge/api-client";
import { useT, t as i18nT } from "../../lib/i18n";
import {
  addressRows, addressFormatFor, countryOptions, validate, emptyDraft,
  resolveCountry, resolveRegion,
  WEEKDAYS, WEEKDAY_LABEL,
  type LocFormDraft, type LocErrors, type LocField, type AddressFieldSpec,
} from "./location-validation";

/* ── Field primitives ───────────────────────────────────────────────────── */

function FieldLabel({ label, required, contractKey }: { label: string; required?: boolean; contractKey?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 5 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
      {required ? <span style={{ fontSize: 12, color: T.red1 }}>*</span> : null}
      <span style={{ flex: 1 }} />
      {contractKey ? <span style={{ fontSize: 10, fontFamily: T.fontMono, color: T.n3, whiteSpace: "nowrap" }}>{contractKey}</span> : null}
    </div>
  );
}

/** A select with the same box, height and error treatment as jrni-ui's Input. */
function FieldSelect({ value, options, onChange, onBlur, error, disabled, placeholder, labelFor }: {
  value: string; options: readonly string[]; onChange: (v: string) => void; onBlur?: () => void;
  error?: string; disabled?: boolean; placeholder: string;
  /** Render a friendly label for an option value (country code → country name). */
  labelFor?: (v: string) => string;
}) {
  const border = error ? T.red1 : T.n4;
  return (
    <div>
      <select
        value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} onBlur={onBlur}
        style={{
          width: "100%", height: 40, borderRadius: 4, border: `1px solid ${border}`,
          background: disabled ? T.n6 : T.white, color: value ? T.n1 : T.n3,
          fontSize: 14, fontFamily: T.font, padding: "0 10px", outline: "none",
          appearance: "none", WebkitAppearance: "none",
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23697180' stroke-width='2.6' stroke-linecap='round'><path d='m6 9 6 6 6-6'/></svg>\")",
          backgroundRepeat: "no-repeat", backgroundPosition: "right 11px center",
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{labelFor ? labelFor(o) : o}</option>)}
      </select>
      {error ? <div style={{ marginTop: 4, fontSize: 12, color: T.red1, lineHeight: 1.4 }}>{error}</div> : null}
    </div>
  );
}

function SectionHead({ icon, title, desc }: { icon: ReactNode; title: string; desc?: string }) {
  return (
    <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${T.border}` }}>
      <span style={{ width: 42, height: 42, borderRadius: 12, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</span>
      <div>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.n1 }}>{title}</h2>
        {desc ? <p style={{ margin: "4px 0 0", fontSize: 13, color: T.soft, maxWidth: 620 }}>{desc}</p> : null}
      </div>
    </div>
  );
}

const timeInput: CSSProperties = {
  height: 32, border: `1px solid ${T.n4}`, borderRadius: 6, padding: "0 8px",
  fontSize: 13, fontFamily: T.font, color: T.n1, background: T.white, outline: "none",
};

/* ── Hours grid ─────────────────────────────────────────────────────────── */

function HoursGrid({ hours, onChange, canWrite, error }: {
  hours: LocFormDraft["hours"]; onChange: (h: LocFormDraft["hours"]) => void; canWrite: boolean; error?: string;
}) {
  const t = useT();
  const dayAt = (day: string) => hours.find((h) => h.day === day) ?? { day, windows: [] };
  const setDay = (day: string, windows: { open: string; close: string }[]) =>
    onChange(WEEKDAYS.map((d) => (d === day ? { day: d, windows } : dayAt(d))));

  return (
    <div>
      <div style={{ border: `1px solid ${error ? T.red1 : T.border}`, borderRadius: 10, overflow: "hidden" }}>
        {WEEKDAYS.map((day, di) => {
          const windows = dayAt(day).windows;
          const off = windows.length === 0;
          return (
            <div key={day} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "9px 12px", borderTop: di ? `1px solid ${T.border}` : "none", background: off ? T.canvas : T.white }}>
              <div style={{ width: 40, fontSize: 12.5, fontWeight: 700, color: off ? T.soft : T.n1, paddingTop: 7 }}>{WEEKDAY_LABEL[day]}</div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                {off ? (
                  <span style={{ fontSize: 12.5, color: T.soft, paddingTop: 7 }}>{t("locations.editorPage.closed", "Closed")}</span>
                ) : windows.map((w, i) => {
                  const bad = !!w.open && !!w.close && w.open >= w.close;
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input type="time" value={w.open} disabled={!canWrite} style={{ ...timeInput, borderColor: bad ? T.red1 : T.n4 }}
                        onChange={(e) => setDay(day, windows.map((x, xi) => (xi === i ? { ...x, open: e.target.value } : x)))} />
                      <span style={{ color: T.soft, fontSize: 13 }}>–</span>
                      <input type="time" value={w.close} disabled={!canWrite} style={{ ...timeInput, borderColor: bad ? T.red1 : T.n4 }}
                        onChange={(e) => setDay(day, windows.map((x, xi) => (xi === i ? { ...x, close: e.target.value } : x)))} />
                      {canWrite ? (
                        <button type="button" onClick={() => setDay(day, windows.filter((_, xi) => xi !== i))}
                          title={t("common.actions.remove", "Remove")}
                          style={{ border: "none", background: "transparent", color: T.n3, cursor: "pointer", display: "inline-flex" }}>
                          <Icon.x size={14} />
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              {canWrite ? (
                <button type="button" onClick={() => setDay(day, [...windows, { open: "09:00", close: "17:00" }])}
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 9px", borderRadius: 6, border: `1px solid ${T.borderStrong}`, background: T.white, color: T.p1, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.font, flexShrink: 0 }}>
                  <Icon.plus size={11} /> {t("locations.editorPage.time", "Time")}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
      {error ? <div style={{ marginTop: 6, fontSize: 12, color: T.red1 }}>{error}</div> : null}
    </div>
  );
}

/* ── Screen ─────────────────────────────────────────────────────────────── */

export default function LocationEditorPage() {
  const t = useT();
  const nav = useNav();
  const { locations, reload } = useLocations();
  const isNew = !!nav.params?.new;
  const viewOnly = !!nav.params?.view;
  const id = nav.params?.id as string | undefined;
  const canWrite = !viewOnly && can("locations.write");

  const [form, setForm] = useState<LocFormDraft>(() => emptyDraft());
  const [errors, setErrors] = useState<LocErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState<"details" | "hours">("details");
  const [locating, setLocating] = useState(false);
  const seededFor = useRef<string | null>(null);

  /* The list hook resolves async: seed when the record ARRIVES, once per id,
     so a later refresh can never clobber what the user has typed. */
  useEffect(() => {
    if (isNew || !id || seededFor.current === id) return;
    const found = locations.find((l) => l.raw?.id === id || l.id === id);
    if (!found) return;
    const r = (found.raw ?? {}) as Record<string, unknown>;
    seededFor.current = id;
    const rawHours = Array.isArray(r.hours) ? (r.hours as { day: string; windows: { open: string; close: string }[] }[]) : [];
    setForm(emptyDraft({
      id: String(r.id ?? id),
      name: String(r.name ?? found.name ?? ""),
      address: String(r.address ?? ""),
      addressLine2: String(r.addressLine2 ?? ""),
      city: String(r.city ?? ""),
      /* Canonicalised to the value the pick-list can actually SELECT.
       *
       * The record stores the subdivision code — the location seed library seeds
       * country:"US" with region:"NY" — while the US/JP/IN/DE pick-lists carry full
       * names. Passing "NY" straight into a <select> whose options are names leaves
       * selectedIndex at -1, so the REQUIRED State field renders blank on a record
       * that is perfectly valid, and the operator's next save writes the empty
       * selection back. resolveRegion accepts either convention in either direction
       * and returns "" only for a genuine non-region, so the raw value is kept when
       * it cannot be mapped rather than being discarded here. */
      region: resolveRegion(String(r.country ?? ""), String(r.region ?? "")) || String(r.region ?? ""),
      postalCode: String(r.postalCode ?? ""),
      /* Canonical alpha-2, so the country select matches too ("USA" -> "US"). */
      country: resolveCountry(String(r.country ?? "")) || String(r.country ?? ""),
      phone: String(r.phone ?? ""),
      timezone: String(r.timezone ?? ""),
      externalCode: String(r.externalCode ?? ""),
      hours: WEEKDAYS.map((d) => rawHours.find((h) => h.day === d) ?? { day: d, windows: [] }),
      hoursSet: !!r.hoursSet,
      active: r.active !== false,
      archived: r.archived === true,
      latitude: typeof r.latitude === "number" ? r.latitude : undefined,
      longitude: typeof r.longitude === "number" ? r.longitude : undefined,
    }));
  }, [id, isNew, locations]);

  const takenCodes = useMemo(
    () => locations
      .filter((l) => (l.raw?.id ?? l.id) !== form.id)
      .map((l) => String((l.raw as { externalCode?: string } | undefined)?.externalCode ?? ""))
      .filter(Boolean),
    [locations, form.id],
  );

  const format = addressFormatFor(form.country);
  const rows = addressRows(form.country);

  const set = (patch: Partial<LocFormDraft>) => {
    setForm((f) => ({ ...f, ...patch }));
    // Clear the error on any field the user is actively fixing.
    const keys = Object.keys(patch) as LocField[];
    setErrors((e) => {
      const next = { ...e };
      for (const k of keys) delete next[k];
      return next;
    });
  };

  const blur = (field: LocField) => {
    setTouched((s) => ({ ...s, [field]: true }));
    const found = validate(form, { only: [field], takenExternalCodes: takenCodes, requireTimeZone: false });
    setErrors((e) => {
      const next = { ...e };
      if (found[field]) next[field] = found[field];
      else delete next[field];
      return next;
    });
  };

  /**
   * Resolve the typed address to coordinates through the geo family's
   * `validateAddress`. A real awaited call — and when the service cannot place
   * the address it says so, verbatim, rather than the UI inventing a pin.
   */
  const locate = async () => {
    if (locating) return;
    if (!form.address.trim() || !form.city.trim()) {
      toast({ tone: "warn", title: t("locations.editorPage.addAnAddressFirst", "Add an address first"), subtitle: t("locations.editorPage.streetAndCityAreNeededToLocate", "Street and city are needed to place a pin.") });
      return;
    }
    setLocating(true);
    try {
      const res = await (validateAddress as unknown as (b: unknown, o: unknown) => Promise<{ valid: boolean; lat?: number; lng?: number; tz?: string; reason?: string }>)(
        {
          street: form.address.trim(),
          city: form.city.trim(),
          ...(form.region.trim() ? { region: form.region.trim() } : {}),
          ...(form.postalCode.trim() ? { postalCode: form.postalCode.trim() } : {}),
          ...(form.country.trim() ? { country: form.country.trim().toUpperCase() } : {}),
        },
        LOCATIONS_OPTS,
      );
      if (res.valid && typeof res.lat === "number" && typeof res.lng === "number") {
        set({ latitude: res.lat, longitude: res.lng, ...(res.tz && !form.timezone ? { timezone: res.tz } : {}) });
        toast({ tone: "success", title: t("locations.editorPage.located", "Located"), subtitle: `${res.lat.toFixed(5)}, ${res.lng.toFixed(5)}` });
      } else {
        toast({ tone: "warn", title: t("locations.editorPage.couldnTPlaceThatAddress", "Couldn't place that address"), subtitle: res.reason ?? "" });
      }
    } catch (e) {
      toast({ tone: "danger", title: t("locations.editorPage.locationLookupFailed", "Location lookup failed"), subtitle: e instanceof Error ? e.message : String(e) });
    } finally {
      setLocating(false);
    }
  };

  const save = async () => {    if (saving) return;
    const found = validate(form, { takenExternalCodes: takenCodes, requireTimeZone: true });
    setErrors(found);
    setTouched((s) => {
      const next = { ...s };
      for (const k of Object.keys(found)) next[k] = true;
      return next;
    });
    if (Object.keys(found).length) {
      const first = Object.keys(found)[0] as LocField;
      setSection(first === "timezone" || first === "hours" ? "hours" : "details");
      toast({
        tone: "danger",
        title: t("locations.editorPage.checkTheHighlightedFields", "Check the highlighted fields"),
        subtitle: found[first],
      });
      return;
    }
    setSaving(true);
    const input = {
      name: form.name.trim(),
      address: form.address.trim() || null,
      addressLine2: form.addressLine2.trim() || null,
      city: form.city.trim() || null,
      region: form.region.trim() || null,
      postalCode: form.postalCode.trim() || null,
      country: form.country.trim() || null,
      phone: form.phone.trim() || null,
      externalCode: form.externalCode.trim() || null,
      timezone: form.timezone.trim() || null,
      hours: form.hours,
      hoursSet: form.hours.some((h) => h.windows.length > 0),
      active: form.active,
      /* Carried explicitly: omitting them on PATCH would silently drop a
         located pin the first time anyone saves an unrelated field. */
      latitude: typeof form.latitude === "number" ? form.latitude : null,
      longitude: typeof form.longitude === "number" ? form.longitude : null,
    };
    try {
      if (isNew || !form.id) {
        await (createLocation as unknown as (i: unknown, o: unknown) => Promise<unknown>)(input, LOCATIONS_OPTS);
      } else {
        await (updateLocation as unknown as (i: string, b: unknown, o: unknown) => Promise<unknown>)(form.id, input, LOCATIONS_OPTS);
      }
      logAction("location", form.id || form.name.trim(), form.name.trim(), isNew ? "created" : "updated");
      reload();
      toast({
        tone: "success",
        title: isNew ? t("locations.editorPage.locationAdded", "Location added") : t("locations.editorPage.locationSaved", "Location saved"),
        subtitle: form.name.trim(),
      });
      setSaving(false);
      nav.go("locations");
    } catch (e) {
      /* A server rejection lands back on the field that caused it — a bare
         toast would leave the user hunting. */
      const msg = e instanceof Error ? e.message : String(e);
      setSaving(false);
      setErrors((prev) => ({ ...prev, name: prev.name ?? undefined } as LocErrors));
      toast({
        tone: "danger",
        title: t("locations.editorPage.couldnTSaveLocation", "Couldn't save location"),
        subtitle: msg || t("locations.editorPage.nothingWasSavedYourEntriesAreStillHereRetryInAMoment", "Nothing was saved — your entries are still here. Retry in a moment."),
      });
    }
  };

  const errFor = (field: LocField) => (touched[field] ? errors[field] : errors[field]);

  const renderField = (spec: AddressFieldSpec) => {
    const value = String((form as Record<string, unknown>)[spec.key] ?? "");
    const error = errFor(spec.key as LocField);
    return (
      <div key={spec.key} style={{ flex: spec.span, minWidth: 0 }}>
        <FieldLabel label={spec.label} required={spec.required} contractKey={spec.key} />
        {spec.options ? (
          <FieldSelect
            value={value} options={spec.options} disabled={!canWrite} error={error}
            placeholder={i18nT("locations.editorPage.choose", "Choose…")}
            onChange={(v) => set({ [spec.key]: v } as Partial<LocFormDraft>)}
            onBlur={() => blur(spec.key as LocField)}
          />
        ) : (
          <Input
            value={value} readOnly={!canWrite} error={error} placeholder={spec.placeholder ?? ""}
            onChange={(e) => set({ [spec.key]: e.target.value } as Partial<LocFormDraft>)}
            onBlur={() => blur(spec.key as LocField)}
          />
        )}
      </div>
    );
  };

  const invalidCount = Object.keys(errors).length;

  return (
    <Screen rail="locations">
      <PageHeader
        title={isNew ? t("locations.locationEditorPage.newLocation", "New location") : (form.name || t("locations.locationEditorPage.editLocation", "Edit location"))}
        subtitle={<span>{format.name}{form.timezone ? ` · ${form.timezone}` : ""}</span>}
        breadcrumb={
          <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("locations")}>
            <Icon.arrow dir="left" size={11} /> {t("locations.editorPage.locations", "Locations")}
          </span>
        }
        actions={viewOnly ? (
          <Btn icon={<Icon.edit size={14} />} onClick={() => nav.go("locations.edit", { id: form.id })}>{t("common.actions.edit", "Edit")}</Btn>
        ) : (
          <>
            <Btn variant="secondary" onClick={() => nav.go("locations")}>{t("common.actions.cancel", "Cancel")}</Btn>
            <Btn icon={<Icon.check size={14} />} onClick={() => void save()} disabled={saving}>
              {saving ? t("locations.locationEditorPage.saving", "Saving…") : isNew ? t("locations.list.addLocation", "Add location") : t("common.actions.save", "Save")}
            </Btn>
          </>
        )}
      />

      <div style={{ flex: 1, overflow: "hidden", display: "flex", minHeight: 0 }}>
        <nav style={{ width: 216, flexShrink: 0, borderRight: `1px solid ${T.border}`, padding: "16px 12px", background: T.canvas }}>
          {([
            { id: "details" as const, label: t("locations.editorPage.detailsAddress", "Details & address"), icon: <Icon.loc size={16} />, bad: (["name", "address", "addressLine2", "city", "region", "postalCode", "country", "phone", "externalCode"] as LocField[]).filter((k) => errors[k]).length },
            { id: "hours" as const, label: t("locations.editorPage.hoursTimeZone", "Hours & time zone"), icon: <Icon.clock size={16} />, bad: (["timezone", "hours"] as LocField[]).filter((k) => errors[k]).length },
          ]).map((s) => {
            const on = section === s.id;
            return (
              <button key={s.id} onClick={() => setSection(s.id)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", marginBottom: 4, borderRadius: 9, border: "none", background: on ? T.white : "transparent", color: on ? T.p1 : T.n2, fontWeight: on ? 700 : 500, fontSize: 13.5, cursor: "pointer", fontFamily: T.font, boxShadow: on ? T.shadowSm : "none", textAlign: "left" }}>
                <span style={{ color: on ? T.p1 : T.soft, display: "inline-flex" }}>{s.icon}</span>
                <span style={{ flex: 1 }}>{s.label}</span>
                {s.bad ? <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: T.red1, borderRadius: 999, minWidth: 18, textAlign: "center", padding: "1px 6px" }}>{s.bad}</span> : null}
              </button>
            );
          })}

          {invalidCount ? (
            <div style={{ marginTop: 14, padding: "10px 11px", borderRadius: 9, background: "#FDECEF", border: "1px solid #F5C9D5" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#B0093A" }}>
                {invalidCount} {invalidCount === 1 ? t("locations.editorPage.fieldNeedsAttention", "field needs attention") : t("locations.editorPage.fieldsNeedAttention", "fields need attention")}
              </div>
              <div style={{ fontSize: 11.5, color: "#8A3050", marginTop: 3, lineHeight: 1.45 }}>
                {t("locations.editorPage.savingIsBlockedUntilTheyRePut", "Saving is blocked until they're put right.")}
              </div>
            </div>
          ) : null}
        </nav>

        <div style={{ flex: 1, overflow: "auto", padding: "22px 26px 60px", minWidth: 0 }}>
          <div style={{ maxWidth: 760 }}>
            {section === "details" ? (
              <div>
                <SectionHead icon={<Icon.loc size={18} />} title={t("locations.editorPage.detailsAddress", "Details & address")}
                  desc={t("locations.editorPage.theCountryPicksTheForm", "The country picks the form — labels, field order and which fields are required all follow it.")} />

                <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                  <div style={{ flex: 2, minWidth: 0 }}>
                    <FieldLabel label={t("locations.editorPage.locationName", "Location name")} required contractKey="name" />
                    <Input value={form.name} readOnly={!canWrite} error={errFor("name")}
                      placeholder={i18nT("locations.editorPage.eGBostonSeaport", "e.g. Boston · Seaport")}
                      onChange={(e) => set({ name: e.target.value })} onBlur={() => blur("name")} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <FieldLabel label={t("locations.editorPage.storeCode", "Store code")} contractKey="externalCode" />
                    <Input value={form.externalCode} readOnly={!canWrite} error={errFor("externalCode")}
                      placeholder="APB-0117"
                      onChange={(e) => set({ externalCode: e.target.value })} onBlur={() => blur("externalCode")} />
                  </div>
                </div>

                <div style={{ marginBottom: 14 }}>
                  <FieldLabel label={t("locations.editorPage.country", "Country")} required contractKey="country" />
                  <div style={{ maxWidth: 320 }}>
                    <FieldSelect
                      value={form.country} disabled={!canWrite} error={errFor("country")}
                      placeholder={i18nT("locations.editorPage.chooseACountry", "Choose a country…")}
                      options={countryOptions().map((c) => c.code)}
                      labelFor={(code) => {
                        const hit = countryOptions().find((c) => c.code === code);
                        return hit ? `${hit.name} (${hit.code})` : code;
                      }}
                      /* Re-canonicalise the region against the NEW country, and
                       * clear it when it does not belong there. Changing US ->
                       * Japan left region holding "New York": the required
                       * Prefecture select had no matching option so it rendered
                       * blank, the required-field check saw a non-empty string and
                       * raised nothing, and Save persisted region "New York" with
                       * country JP. Canonicalising only at hydration cannot catch
                       * this — the country changes after the form has loaded. */
                      onChange={(v) => set({ country: v, region: resolveRegion(v, form.region) })}
                      onBlur={() => blur("country")}
                    />
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: T.soft, maxWidth: 560, lineHeight: 1.45 }}>{format.note}</div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {rows.map((row, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      {row.map(renderField)}
                    </div>
                  ))}
                </div>

                <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 14 }}>
                  <div style={{ flex: 1.2, minWidth: 0 }}>
                    <FieldLabel label={t("locations.editorPage.phone", "Phone")} contractKey="phone" />
                    <Input value={form.phone} readOnly={!canWrite} error={errFor("phone")}
                      placeholder={`${format.dialCode} …`}
                      onChange={(e) => set({ phone: e.target.value })} onBlur={() => blur("phone")} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <FieldLabel label={t("locations.editorPage.status", "Status")} contractKey="active" />
                    <FieldSelect
                      value={form.active ? "Active" : "Inactive"} disabled={!canWrite}
                      placeholder={i18nT("locations.editorPage.status", "Status")}
                      options={["Active", "Inactive"]}
                      onChange={(v) => set({ active: v === "Active" })}
                    />
                  </div>
                </div>

                {/* Map preview — coordinates are data, never guessed. See LocationMap. */}
                <div style={{ marginTop: 20 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 7 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n2 }}>{t("locations.editorPage.onTheMap", "On the map")}</span>
                    <span style={{ flex: 1 }} />
                    {canWrite && typeof form.latitude === "number" ? (
                      <button type="button" onClick={() => void locate()} disabled={locating}
                        style={{ border: "none", background: "transparent", color: T.p1, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font, padding: 0 }}>
                        {locating ? t("locations.editorPage.locating", "Locating…") : t("locations.editorPage.reLocateFromAddress", "Re-locate from address")}
                      </button>
                    ) : null}
                    <span style={{ fontSize: 10, fontFamily: T.fontMono, color: T.n3 }}>latitude · longitude</span>
                  </div>
                  <LocationMap
                    latitude={form.latitude}
                    longitude={form.longitude}
                    label={form.name}
                    caption={[form.address, form.city, form.postalCode, form.country].filter(Boolean).join(", ")}
                    action={canWrite ? (
                      <button type="button" onClick={() => void locate()} disabled={locating}
                        style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 32, padding: "0 13px", borderRadius: 6, border: `1px solid ${T.borderStrong}`, background: T.white, color: T.p1, fontSize: 12.5, fontWeight: 600, cursor: locating ? "default" : "pointer", fontFamily: T.font, marginTop: 2 }}>
                        <Icon.loc size={13} />
                        {locating ? t("locations.editorPage.locating", "Locating…") : t("locations.editorPage.locateFromAddress", "Locate from address")}
                      </button>
                    ) : null}
                  />
                </div>
              </div>
            ) : (
              <div>
                <SectionHead icon={<Icon.clock size={18} />} title={t("locations.editorPage.hoursTimeZone", "Hours & time zone")}
                  desc={t("locations.editorPage.theEngineResolvesEverySchedule", "The engine resolves every schedule to UTC against this zone, and staff signing in here read every time in it.")} />

                <div style={{ marginBottom: 18 }}>
                  <FieldLabel label={t("locations.editorPage.timeZone", "Time zone")} required contractKey="timezone" />
                  <div style={{ maxWidth: 340 }}>
                    {viewOnly
                      ? <Input value={form.timezone || "—"} readOnly />
                      : <TimeZonePicker value={form.timezone} onChange={(v: string) => set({ timezone: v })} />}
                  </div>
                  {errFor("timezone") ? <div style={{ marginTop: 5, fontSize: 12, color: T.red1 }}>{errFor("timezone")}</div> : null}

                  <div style={{ marginTop: 12, borderRadius: 10, background: "#262265", padding: "13px 15px", maxWidth: 560 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: "#B5A8FF" }}>
                      {t("locations.editorPage.pushedToStaff", "Pushed to staff")}
                    </div>
                    <div style={{ marginTop: 7, fontSize: 12.5, lineHeight: 1.55, color: "rgba(255,255,255,.86)" }}>
                      {t("locations.editorPage.everyoneWhoSignsInHere", "Everyone who signs in at this location reads every time — schedules, queues, booking sheets, reports — in its zone. A staff member who has pinned their own zone in their profile keeps their pin; it wins for them, and only for them.")}
                    </div>
                  </div>
                </div>

                <div>
                  <FieldLabel label={t("locations.editorPage.operatingHours", "Operating hours")} contractKey="hours" />
                  {!form.hours.some((h) => h.windows.length) ? (
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", borderRadius: 9, background: "#FFFAED", border: "1px solid #F2E6C7", marginBottom: 10 }}>
                      <span style={{ color: T.orange1, flexShrink: 0, marginTop: 1 }}><Icon.warn size={15} /></span>
                      <div style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.45 }}>
                        {t("locations.editorPage.noOperatingHoursSetUntilYouSetThemTheEngineCanT", "No operating hours set. Until you set them the engine can't clamp this venue's open window — set hours below, then save.")}
                      </div>
                    </div>
                  ) : null}
                  <HoursGrid hours={form.hours} canWrite={canWrite} error={errFor("hours")} onChange={(h) => set({ hours: h, hoursSet: true })} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Screen>
  );
}
